import type { SidebarGroupId } from '@/utils/hooks'
import { Navigate, useLocation } from 'react-router'
import type { SidebarItem } from './sidebar-config'
import { useSidebarConfig } from './sidebar-config'

interface SidebarGroupRedirectProps {
  groupId: SidebarGroupId
}

const isOnItemPage = (item: SidebarItem, pathname: string) =>
  [item.to, ...(item.subPaths ?? [])].some(
    path => pathname === path || pathname.startsWith(`${path}/`)
  )

/**
 * The page a sidebar group's breadcrumb leads to: the group's page the user is
 * on, or else the first page in the group the user can see.
 */
export const useSidebarGroupPath = (groupId: SidebarGroupId) => {
  const config = useSidebarConfig()
  const { pathname } = useLocation()

  const group = config.find(
    item => item.type === 'group' && item.id === groupId
  )
  if (group?.type !== 'group') return undefined

  const current = group.items.find(item => isOnItemPage(item, pathname))
  return (current ?? group.items[0])?.to
}

/**
 * Gives a sidebar group's URL prefix a page of its own, so its breadcrumb lands
 * on the first page in the group the user can see.
 */
export const SidebarGroupRedirect: React.FC<SidebarGroupRedirectProps> = ({
  groupId,
}) => {
  const to = useSidebarGroupPath(groupId)

  return <Navigate to={to ?? '/dashboard'} replace />
}
